import axios from 'axios';

const API_URL =
    (import.meta?.env?.VITE_API_URL || process.env.VUE_APP_API_URL) ||
    'http://localhost:8000/api';

const axiosInstance = axios.create({
    baseURL: API_URL,
    timeout: 20000
});

axiosInstance.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('access_token');
        if (token) config.headers['Authorization'] = `Bearer ${token}`;
        config.headers['Content-Type'] = 'application/json';
        return config;
    },
    (error) => Promise.reject(error)
);

axiosInstance.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            console.error('Authentication error:', error.response.data);
        }
        return Promise.reject(error);
    }
);

function toNumber(value, fallback = 0) {
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
}

function toBoolean(value) {
    return value === true || value === 'true' || value === 1 || value === '1';
}

function normalizeActiveAlarms(value) {
    if (Array.isArray(value)) return value;
    if (!value || value === '[]') return [];

    try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
}

function normalizePersistentAlarms(value) {
    if (Array.isArray(value)) return value.filter(Boolean);
    if (!value || value === '[]') return [];

    try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
    } catch {
        return [];
    }
}

class AlarmApiService {
    async listDevices(opts = {}) {
        const params = new URLSearchParams();
        if (opts.tenantId) params.append('tenant_id', opts.tenantId);

        const qs = params.toString() ? `?${params}` : '';
        const response = await axiosInstance.get(`/alarms/devices${qs}`);
        const devices = Array.isArray(response.data?.devices) ? response.data.devices : [];

        return {
            ...response.data,
            devices: devices.map(this.normalizeDeviceAlarmInfo)
        };
    }

    async listDevicesHistory({ tenantId, fromUnix, toUnix } = {}) {
        const params = new URLSearchParams();
        if (tenantId) params.append('tenant_id', tenantId);
        if (fromUnix != null) params.append('from', String(fromUnix));
        if (toUnix != null) params.append('to', String(toUnix));

        const qs = params.toString() ? `?${params}` : '';
        const response = await axiosInstance.get(`/alarms/devices/history${qs}`);
        const devices = Array.isArray(response.data?.devices) ? response.data.devices : [];

        return {
            ...response.data,
            devices: devices.map((batch) => ({
                ...batch,
                count: toNumber(batch?.count),
                entries: Array.isArray(batch?.entries)
                    ? batch.entries.map(this.normalizeHistoryEntry)
                    : []
            }))
        };
    }

    normalizeDeviceAlarmInfo(device) {
        if (!device || typeof device !== 'object') return null;

        return {
            ...device,
            ldn: device.ldn || null,
            tenant_name: device.tenant_name || null,
            last_alarm_bitmap: toNumber(device.last_alarm_bitmap),
            last_active_alarms: normalizeActiveAlarms(device.last_active_alarms),
            last_leakage_duration: toNumber(device.last_leakage_duration),
            last_pipe_burst_duration: toNumber(device.last_pipe_burst_duration),
            last_backflow_duration: toNumber(device.last_backflow_duration),
            last_temperature_duration: toNumber(device.last_temperature_duration),
            last_dry_duration: toNumber(device.last_dry_duration),
            last_overflow_duration: toNumber(device.last_overflow_duration),
            last_battery_level: toNumber(device.last_battery_level, null),
            has_battery: toBoolean(device.has_battery),
            total_records: toNumber(device.total_records),
            alarm_state: String(device.alarm_state || 'normal').toLowerCase(),
            persistent_alarms: normalizePersistentAlarms(device.persistent_alarms)
        };
    }

    normalizeHistoryEntry(entry) {
        if (!entry || typeof entry !== 'object') return null;

        return {
            ...entry,
            ldn: entry.ldn || null,
            tenant_name: entry.tenant_name || null,
            alarm_bitmap: toNumber(entry.alarm_bitmap),
            active_alarms: normalizeActiveAlarms(entry.active_alarms),
            leakage_duration: toNumber(entry.leakage_duration),
            pipe_burst_duration: toNumber(entry.pipe_burst_duration),
            backflow_duration: toNumber(entry.backflow_duration),
            temperature_duration: toNumber(entry.temperature_duration),
            dry_duration: toNumber(entry.dry_duration),
            overflow_duration: toNumber(entry.overflow_duration),
            battery_level: toNumber(entry.battery_level, null),
            has_battery: toBoolean(entry.has_battery),
            alarm_state: String(entry.alarm_state || 'normal').toLowerCase(),
            persistent_alarms: normalizePersistentAlarms(entry.persistent_alarms)
        };
    }
}

export default new AlarmApiService();
